import React, { useState } from "react";
import { Pressable, StyleSheet, View } from "react-native";
import { AnimatePresence, MotiView } from "moti";

import CustomText from "../../../../../components/common/general/CustomText/CustomText";
import { colors } from "../../../../../styles/design-system";

type TabKey = "review" | "attachments";

interface PanelTabsProps {
  attachmentsCount: number;
  reviewContent: React.ReactNode;
  attachmentsContent: React.ReactNode;
}

function PanelTabs({ attachmentsCount, reviewContent, attachmentsContent }: PanelTabsProps) {
  const [active, setActive] = useState<TabKey>("review");
  const tablistLabel = "Review content tabs";

  return (
    <View>
      <View
        style={styles.tabBar}
        accessibilityRole="tablist"
        accessibilityLabel={tablistLabel}
        role="tablist"
        aria-label={tablistLabel}>
        <TabButton label="Review" isActive={active === "review"} onPress={() => setActive("review")} />
        <TabButton
          label={`Attachments (${attachmentsCount})`}
          isActive={active === "attachments"}
          onPress={() => setActive("attachments")}
        />
      </View>

      <AnimatePresence exitBeforeEnter>
        <MotiView
          key={active}
          from={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ type: "timing", duration: 120 }}>
          {active === "review" ? reviewContent : attachmentsContent}
        </MotiView>
      </AnimatePresence>
    </View>
  );
}

interface TabButtonProps {
  label: string;
  isActive: boolean;
  onPress: () => void;
}

function TabButton({ label, isActive, onPress }: TabButtonProps) {
  return (
    <Pressable
      onPress={onPress}
      style={[styles.tabButton, isActive ? styles.tabButtonActive : styles.tabButtonInactive]}
      accessibilityRole="tab"
      accessibilityState={{ selected: isActive }}
      accessibilityLabel={label}
      role="tab"
      aria-selected={isActive}
      aria-label={label}>
      <CustomText
        text={label}
        useTranslationText={false}
        size="s"
        weight="medium"
        style={isActive ? styles.tabTextActive : styles.tabTextInactive}
      />
    </Pressable>
  );
}

const styles = StyleSheet.create({
  tabBar: {
    flexDirection: "row",
    gap: 24,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
    marginBottom: 16,
  },
  tabButton: {
    paddingVertical: 10,
    borderBottomWidth: 2,
  },
  tabButtonActive: {
    borderBottomColor: colors.brand[600],
  },
  tabButtonInactive: {
    borderBottomColor: "transparent",
  },
  tabTextActive: {
    color: colors.brand[600],
  },
  tabTextInactive: {
    color: colors.grey[600],
  },
});

export default PanelTabs;
